import { createContext, useContext, useEffect, useState } from 'react'

import { redirectToLogin, redirectToLogout } from '../portal'
import { setCurrentUserId } from '../userScope'
import { migrateLegacyKeys } from '../legacyKeyMigration'
import { getSession } from './api'
import { nextAuthAction, clearAuthBounce } from './authBounce'

export const AuthContext = createContext(undefined)

const overlayStyle = {
  position: 'fixed', inset: 0,
  display: 'flex', flexDirection: 'column', gap: 16,
  alignItems: 'center', justifyContent: 'center',
  background: '#0a0a0f', color: '#ccc',
  fontFamily: 'var(--m-font)', fontSize: 13, padding: 24, textAlign: 'center',
}
const buttonStyle = {
  padding: '8px 18px', border: '1px solid #555', borderRadius: 6,
  background: '#1a1a24', color: '#eee', font: 'inherit', cursor: 'pointer',
}

// Shown only when the portal login loop can't be broken automatically (the
// portal keeps declaring us authenticated while our session stays invalid).
// A loud, actionable dead-end beats an infinite silent bounce (CLAUDE.md UX).
function AuthLoopError() {
  const recover = () => {
    // Reset the loop counter and force a full logout so the portal drops its
    // stale session and shows a real login form on the next visit.
    clearAuthBounce()
    redirectToLogout()
  }
  return (
    <div style={overlayStyle}>
      <div style={{ maxWidth: 460, lineHeight: 1.5 }}>
        <strong>Couldn&rsquo;t establish your session.</strong>
        <div style={{ marginTop: 8, color: '#999' }}>
          You appear to be signed in to the portal, but this app&rsquo;s session
          isn&rsquo;t valid &mdash; usually an expired session cookie. Sign out and
          back in to fix it.
        </div>
      </div>
      <button type="button" style={buttonStyle} onClick={recover}>Sign in again</button>
    </div>
  )
}

export function AuthProvider({ children }) {
  const [me, setMe] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const result = await getSession()
      if (cancelled) return
      if (result) {
        // Session established — reset the loop-breaker so a returning user
        // never inherits a stale bounce count.
        clearAuthBounce()
        // Scope localStorage to this user BEFORE rendering unblocks, so every
        // key-touching module reads/writes the namespaced key (ENG-559).
        // ProtectedRoute gates all content on `me`, so nothing has run yet.
        setCurrentUserId(result.id)
        migrateLegacyKeys(result.id)
        setMe(result)
        setLoading(false)
        return
      }
      // No session. Decide based on how many times we've bounced: a first
      // failure is a normal logged-out visit; repeated failures mean the
      // portal is bouncing us back with a stale session (see authBounce).
      const action = nextAuthAction()
      if (action === 'recover') {
        // Portal thinks we're logged in but we aren't — log out to clear its
        // stale session; the following login redirect will show the form.
        redirectToLogout()
      } else if (action === 'stop') {
        // Even a clean logout+login round-trip failed — stop looping and
        // surface it. Leave `me` null and render the error overlay below.
        setError(true)
        setLoading(false)
      } else {
        // Normal path: bounce to portal SSO. Leave loading=true so
        // ProtectedRoute keeps the spinner up while the browser navigates.
        redirectToLogin()
      }
    })()
    return () => { cancelled = true }
  }, [])

  if (error) return <AuthLoopError />

  return (
    <AuthContext.Provider value={{ me, loading, error, setMe }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
